/* build 2026-07-05 renamed to product-upsells-2.js (digest-bust) + image self-heal */

/* ── UPSELL ATTRIBUTION (2026-07-24) ────────────────────────────────────────
   Tags every cart line added from an upsell so the ops hub can attribute real
   revenue to real upsells. The truth then lives on the Shopify ORDER forever —
   no cookies, no session-to-order matching, no analytics script.

   Emits three hidden line-item properties on the add-to-cart request:
     _upsell       pdp | drawer | cart      which shelf it came from
     _upsell_from  <handle> | cart          the product page that drove it
     _upsell_pos   1-based slot in the rail

   ⛔ Underscore-prefixed on purpose: snippets/line-item.liquid skips any
      property whose first character is "_", so none of this is ever visible to
      the customer in the cart, checkout, emails or order status.

   COST: no new request, no new asset, no polling. One delegated click listener
   and one delegated submit listener. Nothing runs until a shopper touches an
   upsell card.

   ⛔ The submit listener MUST stay in the CAPTURE phase. The theme's
      <product-form> binds submit on itself (bubble) and builds
      `new FormData(this)` at submit time — capture runs first, so the hidden
      inputs are already in the DOM when it reads the form. Move this to bubble
      and the properties are silently dropped.

   ⛔ Loaded from BOTH upsell scripts behind this guard, because
      cart-drawer-upsells-2.js is global but product-upsells-2.js is PDP-only
      and either may be disabled independently. Registering twice is a no-op. */
(function () {
  if (window.__llUpsellAttrBound) return;
  window.__llUpsellAttrBound = true;

  var QB_PREFIXES = ['upsell-qb-', 'cdu-qb-'];
  /* quick-buy drawers are portaled out of the rail, so the shelf that opened
     one can't be derived from the DOM later — record it at click time. */
  var pendingByDrawer = Object.create(null);

  function closest(el, sel) {
    return el && el.closest ? el.closest(sel) : null;
  }

  /* Which shelf, which product, which slot — derived from an element inside a card. */
  function originOf(el) {
    var card = closest(el, '.upsell__card, .cart-upsell__card');
    if (!card) return null;
    var host = closest(card, 'product-upsells, cart-drawer-upsells');
    var surface = 'pdp';
    if (host && host.tagName.toLowerCase() === 'cart-drawer-upsells') {
      surface = host.getAttribute('layout') === 'page' ? 'cart' : 'drawer';
    }
    var from = 'cart';
    if (surface === 'pdp') {
      var pu = document.querySelector('product-upsells[product-handle]');
      from = (pu && pu.getAttribute('product-handle')) || '';
    }
    var pos = 0;
    if (host) {
      var sibs = host.querySelectorAll('.upsell__card, .cart-upsell__card');
      for (var i = 0; i < sibs.length; i++) {
        if (sibs[i] === card) { pos = i + 1; break; }
      }
    }
    return { surface: surface, handle: card.getAttribute('data-handle') || '', from: from, pos: pos };
  }

  function isQbId(id) {
    for (var i = 0; i < QB_PREFIXES.length; i++) if (id.indexOf(QB_PREFIXES[i]) === 0) return true;
    return false;
  }

  /* Sized products open a quick-buy drawer instead of submitting inline.
     Remember which card opened which drawer. */
  document.addEventListener('click', function (event) {
    try {
      var btn = closest(event.target, '[aria-controls]');
      if (!btn) return;
      var id = btn.getAttribute('aria-controls') || '';
      if (!isQbId(id)) return;
      var info = originOf(btn);
      if (info) pendingByDrawer[id] = info;
    } catch (e) { /* attribution must never break a click */ }
  }, true);

  function addProp(form, name, value) {
    var input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'properties[' + name + ']';
    input.value = value;
    form.appendChild(input);
  }

  document.addEventListener('submit', function (event) {
    try {
      var form = event.target;
      if (!form || form.tagName !== 'FORM') return;
      if ((form.getAttribute('action') || '').indexOf('cart/add') === -1) return;
      /* drawer forms persist and can be submitted repeatedly — tag once */
      if (form.querySelector('input[name="properties[_upsell]"]')) return;

      var info = originOf(form);
      if (!info) {
        var drawer = closest(form, 'quick-buy-drawer');
        var id = drawer ? drawer.getAttribute('id') || '' : '';
        if (id) info = pendingByDrawer[id];
      }
      if (!info) return; /* a normal add-to-cart — leave it completely alone */

      addProp(form, '_upsell', info.surface);
      if (info.from) addProp(form, '_upsell_from', info.from);
      if (info.pos) addProp(form, '_upsell_pos', String(info.pos));
    } catch (e) { /* attribution must never block an add to cart */ }
  }, true);
})();

/**
 * Product Upsells — App-managed upsell carousel on the product page
 *
 * Reads pre-built product data from product-upsells.json (pushed by the
 * League Lids ops hub app via Shopify Asset API). The JSON contains full
 * product data including live availability — kept fresh by webhooks.
 *
 * ZERO storefront API calls for product data.
 * Only fetches: config JSON (CDN cached) + /cart.js (for in-cart sorting).
 *
 * BUY CONTROLS ARE THE THEME'S OWN (2026-07-03): each card renders the
 * LITERAL native quick-buy — a `<form is="product-form">` for single-variant
 * products and a `<quick-buy-drawer handle="...">` + aria-controls button for
 * multi-variant products — exactly the markup Impact's product cards emit.
 * The theme handles the drawer animation, variant picking, add-to-cart and
 * the cart notification popup, so the upsell add feels identical to adding
 * from a home-page card. Drawers render as SIBLINGS of the carousel (never
 * inside the animated cards — CSS transforms would trap the fixed overlay).
 * Made-to-order (pod) products link to their page instead ("Customise") —
 * they need personalisation + the made-to-order acknowledgment.
 */

if (!customElements.get('product-upsells')) {
  /* Judge.me preview stars on upsell cards (2026-07-24, self-rendered).
     jdgm.batchRenderBadges can NOT resolve ratings for dynamically-injected
     badges — it fills them all with 0.00 (proven live) — so we render the
     SAME `.jdgm-prev-badge` markup the home/collection cards get server-side
     from the judgeme.badge metafield, using real rating data: the upsell
     catalog's rating fields when present, else the tiny theme asset
     assets/ll-ratings.json (a snapshot of every product's reviews.rating
     metafield). Judge.me's own badge CSS + star font style it, so it's
     pixel-identical to the home cards. 0-review products render nothing —
     identical to home (Judge.me hides 0.00 badges there). Decoration only:
     every failure path leaves the card exactly as it was.
     ⚠ KEEP IN SYNC with the identical helpers in cart-drawer-upsells-2.js. */
  var llRatingsMap = null;
  var llRatingsReq = null;
  function llFetchRatings(configUrl) {
    if (llRatingsMap) return Promise.resolve(llRatingsMap);
    if (!llRatingsReq) {
      var url = String(configUrl || '').replace(/product-upsells\.json/, 'll-ratings.json');
      llRatingsReq = (url && url !== configUrl
        ? fetch(url).then(function (r) { return r.ok ? r.json() : {}; })
        : Promise.resolve({}))
        .then(function (m) { llRatingsMap = m && typeof m === 'object' ? m : {}; return llRatingsMap; })
        .catch(function () { llRatingsMap = {}; return llRatingsMap; });
    }
    return llRatingsReq;
  }
  function llStarSpans(avg) {
    var rounded = Math.round(avg * 2) / 2;
    var html = '';
    for (var i = 1; i <= 5; i++) {
      var cls = rounded >= i ? 'jdgm--on' : (rounded >= i - 0.5 ? 'jdgm--half' : 'jdgm--off');
      html += '<span class="jdgm-star ' + cls + '"></span>';
    }
    return html;
  }
  function llRenderBadge(el, avg, n) {
    el.innerHTML =
      '<div class="jdgm-prev-badge" data-average-rating="' + avg.toFixed(2) + '" data-number-of-reviews="' + n + '" data-number-of-questions="0">' +
        '<span class="jdgm-prev-badge__stars" data-score="' + avg + '" tabindex="-1" aria-label="' + avg + ' stars" role="img">' + llStarSpans(avg) + '</span>' +
        ' <span class="jdgm-prev-badge__text">' + n + ' review' + (n === 1 ? '' : 's') + '</span>' +
      '</div>';
  }
  function llHydrateReviewBadges(scope, configUrl) {
    try {
      var nodes = Array.prototype.slice.call(scope.querySelectorAll('.ll-upsell-rating:not([data-ll-ready])'));
      if (!nodes.length) return;
      llFetchRatings(configUrl).then(function (map) {
        nodes.forEach(function (el) {
          try {
            el.setAttribute('data-ll-ready', '');
            var avg = parseFloat(el.getAttribute('data-rating') || '');
            var n = parseInt(el.getAttribute('data-rating-count') || '', 10);
            if (!(avg > 0) || !(n > 0)) {
              var rec = map[el.getAttribute('data-handle-src') || ''];
              if (rec && rec.length === 2) { avg = Number(rec[0]); n = Number(rec[1]); }
            }
            if (avg > 0 && n > 0) llRenderBadge(el, avg, n);
          } catch (e) { /* card stays as-is */ }
        });
      });
    } catch (e) { /* decoration only — never break the carousel */ }
  }

  /* ⚠ KEEP IN SYNC with the identical function in cart-drawer-upsells.js.
     Normalises the app-pushed config to the v2 shape the widgets consume:
     1. v3 (catalog + handle lists) is expanded — each page's upsell handles
        resolve to shared catalog entries (the compact format).
     2. Prices are converted ONCE to the visitor's display currency:
        - exact embedded AU-market prices for AUD shoppers (price lists resolved),
        - Shopify's market conversion rate for any other non-shop currency.
     Returns null when the config is unusable. */
  function _normalizeUpsellConfig(config) {
    if (!config || !config.products) return null;
    if (config.v === 3 && config.catalog) {
      var expanded = {};
      for (var key in config.products) {
        var e = config.products[key];
        var ups = [];
        for (var i = 0; i < (e.upsells || []).length; i++) {
          var c = config.catalog[e.upsells[i]];
          if (c) ups.push(c);
        }
        if (ups.length) expanded[key] = { heading: e.heading, upsells: ups };
      }
      config = { v: 2, settings: config.settings || {}, products: expanded };
    }
    if (config.v !== 2) return null;

    var cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || '';
    var shopCur = (config.settings && config.settings.shop_currency) || 'NZD';
    var rate = parseFloat(window.Shopify && Shopify.currency && Shopify.currency.rate) || 1;
    if (cur && cur !== shopCur) {
      for (var h in config.products) {
        var entry = config.products[h];
        for (var j = 0; j < entry.upsells.length; j++) {
          var vs = entry.upsells[j].variants || [];
          for (var k = 0; k < vs.length; k++) {
            var v = vs[k];
            if (v._converted) continue;
            v._converted = true;
            if (cur === 'AUD' && v.price_aud != null) {
              v.price = v.price_aud;
              v.compare_price = v.compare_aud != null ? v.compare_aud : null;
            } else {
              v.price = Math.round(v.price * rate);
              if (v.compare_price != null) v.compare_price = Math.round(v.compare_price * rate);
            }
          }
        }
      }
    }
    return config;
  }

  class ProductUpsells extends HTMLElement {
    static _configCache = null;
    static _configPromise = null;
    static _cartVariantIds = null;

    connectedCallback() {
      if (this._initialized) return;

      /* De-dupe guard: if a relocated instance already lives on the page
         (e.g. Impact's product-rerender re-injected the inline copy after a
         variant change), the newcomer removes itself. */
      var existing = document.querySelector('product-upsells[data-relocated]');
      if (existing && existing !== this) { this.remove(); return; }

      this._handle = this.getAttribute('product-handle') || '';
      this._configUrl = this.getAttribute('config-url');
      if (!this._configUrl || !this._handle) return;
      this._initialized = true;
      this._abortController = new AbortController();
      this._batchSize = 8;
      this._renderedCount = 0;
      this._init();
    }

    disconnectedCallback() {
      if (this._skipReconnect) return;
      if (this._abortController) this._abortController.abort();
      if (this._observer) this._observer.disconnect();
      if (this._resizeObserver) this._resizeObserver.disconnect();
      if (this._onDragMove) document.removeEventListener('mousemove', this._onDragMove);
      if (this._onDragEnd) document.removeEventListener('mouseup', this._onDragEnd);
      this._initialized = false;
    }

    async _init() {
      try {
        var signal = this._abortController.signal;
        var config = await this._fetchConfig(signal);

        /* Normaliser guarantees a v2-shaped config (or null) */
        if (!config || !config.products) return;

        /* Direct lookup by product handle */
        var entry = config.products[this._handle];
        if (!entry || !entry.upsells || entry.upsells.length === 0) return;

        /* Get cart contents to sort already-added products last */
        var cartIds = await this._fetchCartVariantIds(signal);

        /* Build product list from JSON — trust availability from the app.
           Only include products with at least one available variant. */
        var products = [];
        for (var i = 0; i < entry.upsells.length; i++) {
          var p = entry.upsells[i];
          if (!p.handle || !p.variants || p.variants.length === 0) continue;
          if (p.available === false) continue;
          if (!p.variants.some(function(v) { return v.available; })) continue;
          products.push(p);
        }

        if (products.length === 0) return;

        /* Sort: not-in-cart first, in-cart last */
        products.sort(function(a, b) {
          var aInCart = a.variants.some(function(v) { return v.available && cartIds.has(v.id); });
          var bInCart = b.variants.some(function(v) { return v.available && cartIds.has(v.id); });
          if (aInCart && !bInCart) return 1;
          if (!aInCart && bInCart) return -1;
          return 0;
        });

        this._heading = entry.heading || (config.settings && config.settings.fallback_heading) || 'You May Also Like';
        this._allProducts = products;
        this._cartIds = cartIds;

        /* Break out of the narrow info column: sit full-width below the
           product grid (after the product-rerender wrapper so Impact's
           partial re-renders never touch us). */
        this._relocate();

        /* Render shell + first batch */
        this._renderShell();
        this._renderBatch();
        this.setAttribute('data-loaded', '');
        this._setupLazyLoad();
        this._updateNav();

        /* Image self-heal (2026-07-05): Safari can restore a page from the
           back-forward cache with image loads that were aborted mid-flight —
           the <img> then sits blank forever (complete=true, naturalWidth=0).
           Re-kick any such image after render and on every bfcache restore. */
        var selfHeal = this._verifyImages.bind(this);
        setTimeout(selfHeal, 1500);
        setTimeout(selfHeal, 4000);
        if (!this._pageshowBound) {
          this._pageshowBound = true;
          window.addEventListener('pageshow', function (e) { if (e.persisted) selfHeal(); });
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.warn('[product-upsells] Init failed:', err);
        }
      }
    }

    _relocate() {
      try {
        var rerender = this.closest('product-rerender');
        if (!rerender || !rerender.parentElement) return;
        /* Land directly after the product hero grid. The description moved
           INSIDE the grid (below the gallery, 2026-07-23) — anchoring to
           .ll-rundown would drag the carousel into the grid, so the rerender
           wrapper itself is the anchor now. */
        var anchor = rerender;
        this._skipReconnect = true;
        anchor.after(this);
        this._skipReconnect = false;
        this.setAttribute('data-relocated', '');
      } catch (e) {
        this._skipReconnect = false;
      }
    }

    /* --------------------------------------------------------------- */
    /*  Data fetching — config JSON (CDN) + cart state only            */
    /* --------------------------------------------------------------- */

    async _fetchConfig(signal) {
      if (ProductUpsells._configCache) return ProductUpsells._configCache;
      if (!ProductUpsells._configPromise) {
        ProductUpsells._configPromise = fetch(this._configUrl, { signal: signal })
          .then(function(r) { return r.json(); })
          .then(function(data) {
            var normalized = _normalizeUpsellConfig(data);
            ProductUpsells._configCache = normalized;
            return normalized;
          })
          .catch(function() { return null; });
      }
      return ProductUpsells._configPromise;
    }

    async _fetchCartVariantIds(signal) {
      if (ProductUpsells._cartVariantIds) return ProductUpsells._cartVariantIds;
      try {
        var res = await fetch(Shopify.routes.root + 'cart.js', { signal: signal });
        var cart = await res.json();
        var ids = new Set();
        cart.items.forEach(function(item) { ids.add(item.variant_id); });
        ProductUpsells._cartVariantIds = ids;
        return ids;
      } catch (e) {
        return new Set();
      }
    }

    /* --------------------------------------------------------------- */
    /*  Rendering                                                       */
    /* --------------------------------------------------------------- */

    _renderShell() {
      var html = '<div class="upsell">';
      html += '<div class="upsell__header">';
      html += '<h2 class="upsell__heading">' + this._esc(this._heading) + '</h2>';
      html += '</div>';
      html += '<div class="upsell__stage" data-upsell-nav>';
      html += '<button type="button" class="upsell__nav-btn upsell__nav-btn--prev" data-upsell-prev aria-label="Previous products" disabled>';
      html += '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>';
      html += '</button>';
      html += '<div class="upsell__grid" tabindex="0" aria-label="' + this._esc(this._heading) + '"></div>';
      html += '<button type="button" class="upsell__nav-btn upsell__nav-btn--next" data-upsell-next aria-label="More products">';
      html += '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6"/></svg>';
      html += '</button>';
      html += '</div>';
      html += '<div class="upsell__progress" data-upsell-progress hidden><span class="upsell__progress-fill"></span></div>';
      /* Quick-buy drawers live OUTSIDE the animated cards (transform-safe).
         The container must NOT be display:none — the drawers inside are
         fixed-position overlays the theme shows/hides itself. */
      html += '<div class="upsell__drawers" data-upsell-drawers></div>';
      html += '</div>';

      this.innerHTML = html;
      this._bindEvents();
      this._bindNav();
    }

    _renderBatch() {
      var grid = this.querySelector('.upsell__grid');
      var drawers = this.querySelector('[data-upsell-drawers]');
      if (!grid) return;
      var sentinel = grid.querySelector('.upsell__sentinel');
      if (sentinel) sentinel.remove();

      var start = this._renderedCount;
      var end = Math.min(start + this._batchSize, this._allProducts.length);
      var cardsHtml = '';
      var drawersHtml = '';

      for (var i = start; i < end; i++) {
        var built = this._renderCard(this._allProducts[i], i - start);
        cardsHtml += built.card;
        drawersHtml += built.drawer;
      }
      this._renderedCount = end;
      grid.insertAdjacentHTML('beforeend', cardsHtml);
      if (drawers && drawersHtml) drawers.insertAdjacentHTML('beforeend', drawersHtml);

      if (this._renderedCount < this._allProducts.length) {
        grid.insertAdjacentHTML('beforeend', '<div class="upsell__sentinel"></div>');
        if (this._observer) {
          var newSentinel = grid.querySelector('.upsell__sentinel');
          if (newSentinel) this._observer.observe(newSentinel);
        }
      }
      this._updateNav();
      llHydrateReviewBadges(grid, this._configUrl);
    }

    _renderCard(product, staggerIndex) {
      var variant = this._getFirstAvailableVariant(product);
      if (!variant) return { card: '', drawer: '' };

      var imageUrl = product.image || '';
      var src = imageUrl, srcset = '';
      if (imageUrl) {
        var q = imageUrl.indexOf('?') >= 0 ? '&' : '?';
        src = imageUrl + q + 'width=600';
        srcset = imageUrl + q + 'width=400 400w, ' +
                 imageUrl + q + 'width=600 600w, ' +
                 imageUrl + q + 'width=800 800w';
      }

      var price = this._formatPrice(variant.price);
      var comparePrice = variant.compare_price && variant.compare_price > variant.price
        ? this._formatPrice(variant.compare_price) : '';

      var inCart = product.variants.some(function(v) { return ProductUpsells._cartVariantIds && ProductUpsells._cartVariantIds.has(v.id); });
      var delay = Math.min((staggerIndex || 0) * 0.07, 0.5);
      var cardClasses = 'upsell__card';
      if (inCart) cardClasses += ' upsell__card--in-cart';

      var url = Shopify.routes.root + 'products/' + this._esc(product.handle);
      var html = '<div class="' + cardClasses + '" data-handle="' + this._esc(product.handle) + '" style="animation-delay: ' + delay.toFixed(2) + 's">';

      /* Image */
      html += '<a href="' + url + '" class="upsell__card-image" tabindex="-1" aria-hidden="true">';
      if (src) {
        /* Eager-load the first visible cards (lazy images inside a horizontal
           scroller are unreliable on mobile Safari); fall back to the raw
           image URL if a sized variant ever fails to load. */
        /* No lazy in a horizontal scroller: Safari never triggers lazy
           loads on sideways scroll, so swiped-to cards stayed blank. The
           rail is small (~12 cards x ~20KB) - load everything. */
        var loadMode = 'eager';
        html += '<img src="' + this._esc(src) + '"' + (srcset ? ' srcset="' + this._esc(srcset) + '" sizes="(max-width: 699px) 45vw, (max-width: 999px) 30vw, 22vw"' : '') + ' alt="" loading="' + loadMode + '" decoding="async" width="600" height="600" onerror="this.onerror=null;this.removeAttribute(&quot;srcset&quot;);this.src=this.dataset.raw" data-raw="' + this._esc(imageUrl) + '" />';
      }
      if (comparePrice) html += '<span class="upsell__badge upsell__badge--sale">Sale</span>';
      html += '<span class="upsell__badge upsell__badge--in-cart"><svg width="10" height="10" viewBox="0 0 16 16" fill="none"><path d="M3 8.5l3.5 3.5L13 4" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg> In cart</span>';
      html += '</a>';

      /* Info */
      html += '<div class="upsell__card-info">';
      html += '<a href="' + url + '" class="upsell__card-title">' + this._esc(product.title) + '</a>';

      /* Judge.me preview stars (2026-07-24) — same badge markup the home /
         collection cards carry, self-rendered post-render by
         llHydrateReviewBadges() from real rating data (catalog fields when
         present, else assets/ll-ratings.json). 0-review products stay empty
         — identical to home. */
      html += '<div class="jdgm-widget jdgm-preview-badge ll-upsell-rating"' +
        (product.rating && product.rating_count ? ' data-rating="' + product.rating + '" data-rating-count="' + product.rating_count + '"' : '') +
        ' data-handle-src="' + this._esc(product.handle) + '"></div>';

      /* Price */
      html += '<div class="upsell__card-price">';
      if (comparePrice) html += '<span class="upsell__card-compare">' + comparePrice + '</span>';
      html += '<span>' + price + '</span>';
      html += '</div>';

      /* Buy control — the theme's LITERAL native quick-buy (same markup the
         home-page product cards emit; theme.js provides all behaviour). */
      var drawerHtml = '';
      if (product.pod) {
        /* Made-to-order: personalisation + acknowledgment live on the page */
        html += '<a href="' + url + '" class="button button--outline upsell__buy">Customise</a>';
      } else if (product.variants.length === 1) {
        html += '<form method="post" action="' + Shopify.routes.root + 'cart/add" is="product-form" class="upsell__buy-form">';
        html += '<input type="hidden" name="id" value="' + variant.id + '">';
        html += '<button type="submit" is="custom-button" class="button upsell__buy">Add to cart</button>';
        html += '</form>';
      } else {
        var qbId = 'upsell-qb-' + this._esc(product.handle);
        html += '<button type="button" is="custom-button" class="button upsell__buy" aria-controls="' + qbId + '" aria-expanded="false">Add to cart</button>';
        drawerHtml = '<quick-buy-drawer id="' + qbId + '" header-bordered open-from="bottom" handle="' + this._esc(product.handle) + '?variant=' + variant.id + '" role="region" aria-live="polite" class="quick-buy-drawer drawer"></quick-buy-drawer>';
      }

      html += '</div>'; /* card-info */
      html += '</div>'; /* card */
      return { card: html, drawer: drawerHtml };
    }

    _verifyImages() {
      this.querySelectorAll('.upsell__card-image img').forEach(function (img) {
        /* A healthy image has naturalWidth > 0. complete=false this long
           after render, or complete with 0 width, means the load died —
           re-kick it (once per state; resetting src restarts the fetch). */
        if (img.complete && img.naturalWidth > 0) return;
        var u = img.currentSrc || img.src;
        if (!u) return;
        var srcset = img.getAttribute('srcset');
        img.removeAttribute('srcset');
        img.src = '';
        img.src = u;
        if (srcset) img.setAttribute('srcset', srcset);
      });
    }

    _getFirstAvailableVariant(product) {
      for (var i = 0; i < product.variants.length; i++) {
        if (product.variants[i].available) return product.variants[i];
      }
      return null;
    }

    /* --------------------------------------------------------------- */
    /*  Arrows + scroll progress                                        */
    /* --------------------------------------------------------------- */

    _bindNav() {
      var self = this;
      var grid = this.querySelector('.upsell__grid');
      var prev = this.querySelector('[data-upsell-prev]');
      var next = this.querySelector('[data-upsell-next]');
      if (!grid || !prev || !next) return;

      var page = function() { return Math.max(grid.clientWidth * 0.85, 200); };
      prev.addEventListener('click', function() { grid.scrollBy({ left: -page(), behavior: 'smooth' }); });
      next.addEventListener('click', function() { grid.scrollBy({ left: page(), behavior: 'smooth' }); });

      var ticking = false;
      grid.addEventListener('scroll', function() {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function() { ticking = false; self._updateNav(); });
      }, { passive: true });

      if ('ResizeObserver' in window) {
        this._resizeObserver = new ResizeObserver(function() { self._updateNav(); });
        this._resizeObserver.observe(grid);
      }
    }

    _updateNav() {
      var grid = this.querySelector('.upsell__grid');
      var progress = this.querySelector('[data-upsell-progress]');
      if (!grid) return;

      var maxScroll = grid.scrollWidth - grid.clientWidth;
      var overflowing = maxScroll > 4;
      /* Only the ARROWS + progress react to overflow — never hide the stage
         itself (it contains the products). */
      var prev = this.querySelector('[data-upsell-prev]');
      var next = this.querySelector('[data-upsell-next]');
      if (progress) progress.hidden = !overflowing;
      if (prev) { prev.hidden = !overflowing; }
      if (next) { next.hidden = !overflowing; }
      if (!overflowing) return;

      if (prev) prev.disabled = grid.scrollLeft <= 4;
      if (next) next.disabled = grid.scrollLeft >= maxScroll - 4;

      var fill = this.querySelector('.upsell__progress-fill');
      if (fill) {
        var ratio = Math.min(Math.max(grid.scrollLeft / maxScroll, 0), 1);
        var visibleFrac = Math.max(grid.clientWidth / grid.scrollWidth, 0.08);
        fill.style.width = (visibleFrac * 100).toFixed(2) + '%';
        fill.style.transform = 'translateX(' + (ratio * (100 / visibleFrac - 100)).toFixed(2) + '%)';
      }
    }

    /* --------------------------------------------------------------- */
    /*  Lazy loading via IntersectionObserver                          */
    /* --------------------------------------------------------------- */

    _setupLazyLoad() {
      if (this._renderedCount >= this._allProducts.length) return;
      var self = this;
      var grid = this.querySelector('.upsell__grid');
      this._observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            self._renderBatch();
            if (self._renderedCount >= self._allProducts.length && self._observer) {
              self._observer.disconnect();
            }
          }
        });
      }, { root: grid, rootMargin: '0px 600px 0px 600px' });
      var sentinel = this.querySelector('.upsell__sentinel');
      if (sentinel) this._observer.observe(sentinel);
    }

    /* --------------------------------------------------------------- */
    /*  Drag-to-scroll (desktop) — suppresses clicks after a drag       */
    /* --------------------------------------------------------------- */

    _bindEvents() {
      var self = this;
      var grid = this.querySelector('.upsell__grid');
      if (!grid) return;

      var isDragging = false, startX = 0, scrollStart = 0, dragDist = 0;
      var DRAG_THRESHOLD = 5;

      grid.addEventListener('dragstart', function(e) { e.preventDefault(); });

      grid.addEventListener('mousedown', function(e) {
        if (e.button !== 0) return;
        isDragging = true; dragDist = 0;
        startX = e.pageX; scrollStart = grid.scrollLeft;
      });

      self._onDragMove = function(e) {
        if (!isDragging) return;
        var dx = e.pageX - startX;
        dragDist = Math.abs(dx);
        grid.scrollLeft = scrollStart - dx;
        if (dragDist > DRAG_THRESHOLD) grid.classList.add('upsell__grid--grabbing');
      };

      self._onDragEnd = function() {
        if (!isDragging) return;
        isDragging = false;
        grid.classList.remove('upsell__grid--grabbing');
        if (dragDist > DRAG_THRESHOLD) {
          self._wasDragged = true;
          setTimeout(function() { self._wasDragged = false; }, 50);
        }
      };

      document.addEventListener('mousemove', self._onDragMove);
      document.addEventListener('mouseup', self._onDragEnd);

      /* After a drag, swallow the click so links/buttons don't fire */
      grid.addEventListener('click', function(e) {
        if (self._wasDragged) { e.preventDefault(); e.stopPropagation(); }
      }, true);
    }

    /* --------------------------------------------------------------- */
    /*  Helpers                                                         */
    /* --------------------------------------------------------------- */

    _formatPrice(cents) {
      /* Prices were already converted to the display currency by
         _normalizeUpsellConfig. Match the theme's money-with-currency
         format exactly: "$49.99 NZD" (symbol + amount + space + code). */
      var code = (Shopify.currency && Shopify.currency.active) || 'USD';
      if (!this._currencyFormatter) {
        try {
          this._currencyFormatter = new Intl.NumberFormat(Shopify.locale || 'en', {
            style: 'currency',
            currency: code,
            currencyDisplay: 'narrowSymbol'
          });
        } catch (e) {
          this._currencyFormatter = new Intl.NumberFormat(Shopify.locale || 'en', {
            style: 'currency',
            currency: code
          });
        }
      }
      return this._currencyFormatter.format(cents / 100) + ' ' + code;
    }

    _esc(str) {
      if (!str) return '';
      var d = document.createElement('div');
      d.textContent = str;
      return d.innerHTML;
    }
  }

  customElements.define('product-upsells', ProductUpsells);
}
